import { View, Views } from 'react-big-calendar';

/**
 * Computes the inclusive [start, end] date range that react-big-calendar
 * renders for a given view + anchor date.
 *
 * All returned dates are set to SOD (00:00:00) / EOD (23:59:59.999) so that
 * the backend filter `min_start_time` / `max_end_time` is fully inclusive.
 */
export const getCalendarRange = (view: View, date: Date): { start: Date; end: Date } => {
  switch (view) {
    case Views.MONTH: {
      // First day of the displayed month
      const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      // enUS locale → week starts on Sunday (getDay() === 0)
      const start = new Date(firstOfMonth);
      start.setDate(firstOfMonth.getDate() - firstOfMonth.getDay()); // back to Sunday
      start.setHours(0, 0, 0, 0);

      // Last day of the displayed month
      const lastOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      const end = new Date(lastOfMonth);
      end.setDate(lastOfMonth.getDate() + (6 - lastOfMonth.getDay())); // forward to Saturday
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    case Views.WEEK: {
      const start = new Date(date);
      start.setDate(date.getDate() - date.getDay()); // back to Sunday
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(start.getDate() + 6); // forward to Saturday
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    case Views.DAY: {
      const start = new Date(date);
      start.setHours(0, 0, 0, 0);

      const end = new Date(date);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    case Views.AGENDA: {
      // Agenda shows range: date through date + agendaLength (inclusive)
      // When agendaLength is 0, it shows only the single day
      // Note: agendaLength comes from caller via state, we handle default here
      const start = new Date(date);
      start.setHours(0, 0, 0, 0);

      // For agenda, we return the base date; the actual end will be calculated
      // by caller based on agendaLength. We return a single day by default.
      const end = new Date(date);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    default:
      // Fallback for any unhandled views
      return {
        start: new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0),
        end: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999),
      };
  }
};
